/**
 * The ONE producer of a blueprint folder's compare scope.
 *
 * F07 / W6.1-routed finding — reconcile a folder→DB compare with the scope the
 * blueprint folder was actually emitted under.
 *
 * The folder emitter (`electron/database-blueprint/ddl-emit/sharedEmitter.ts`)
 * honours three settings from `jamsql-blueprint.json` — `includedSchemas`,
 * `excludedObjects` (via its `isExcluded` guard) and `includedObjectTypes` (via
 * `blueprintEmitsFiles`). A compare's TARGET side is a live-DB fetch that
 * honoured none of them, so every object outside the blueprint's scope comes
 * back as target-only → classified `removed` by `schemaComparer`'s target-only
 * pass → scriptable (`isScriptableChangeType` counts `removed`) →
 * `generateDropTable` / `generateDropView` / `generateDropRoutine`. On a
 * 15-database sweep that is the reporter's actual workflow, multiplied by 15.
 *
 * Covered here: `includedSchemas` → an `include` schema filter for BOTH
 * endpoints (the same shape the MCP verb uses), and `includedObjectTypes` →
 * a per-type CAP on the compare's include flags.
 *
 * NOT covered here, and deliberately: `excludedObjects` (S2 / #30). It does not
 * need to be threaded by any caller — `folderSchemaFetcher` reads
 * `jamsql-blueprint.json` during the folder fetch and records the exclusions
 * onto the snapshot as `SchemaSnapshot.excludedObjects`, which `compareSchemas`
 * composes into `declaredScope` and the script generator refuses whole-object
 * `DROP`s on.
 *
 * ---
 *
 * #89 — why this is a MODULE and not a private function in
 * `schemaCompareNavigation.ts`, where it was born.
 *
 * `excludedObjects` (above) is inherited by every caller because it is applied
 * at the FETCH, so no producer can forget it. The other two halves are applied
 * at the REQUEST, so every producer has to build them — and one of the three
 * never did:
 *
 *   - `openSchemaCompareWithFolderAsSource` (single-link "Apply schema to DB")
 *     built them;
 *   - `electron/mcp-blueprint/blueprint.ts` `blueprintApplySchema` builds its
 *     own copy in the main process, with a comment asking that the two "be
 *     kept in step";
 *   - `useApplyToLinkedDbs` (the "Apply schema to linked DBs…" batch) built
 *     NEITHER, and carried a comment claiming its endpoint shape and the tab
 *     the row's own "Open compare" button opens "can never disagree". They
 *     disagreed on both halves: the batch read the whole target database, so
 *     every object outside the blueprint's schemas — and every object of a
 *     type the folder does not model — read `removed`, and every link reported
 *     "changed" on every run, for ever, while its "Open compare" button opened
 *     a correctly-scoped tab that said otherwise.
 *
 * A comment asking two call sites to stay in step is a convention; the third
 * call site is what a convention costs. The renderer's two producers now share
 * this module, so a fourth cannot be written without it. The main-process copy
 * stays where it is — `electron/` imports `workspaceTypes.ts` type-only today,
 * and importing this module's VALUES would pull that whole renderer types file
 * into the main bundle to de-duplicate a dozen-odd lines — but it is no longer
 * half of a two-member handshake.
 */

import { readBlueprintMetadata } from '../../../database-blueprint/services/databaseBlueprintApi';
import type {
  SchemaCompareOptions,
  SchemaCompareScopeCapKey,
  SchemaFilter,
} from '../../core/types/workspaceTypes';
import {
  blueprintEmitsFiles,
  engineHasSequences,
  engineHasUserTypes,
  manifestRecordsUserTypeFiles,
  type BlueprintEmittedTypeKey,
  type BlueprintObjectTypes,
} from '../../../database-blueprint/types/databaseBlueprintTypes';

/**
 * The emitter thinks in object types; Schema Compare thinks in option flags.
 * One table, so a new type cannot be added to one side and forgotten on the
 * other.
 */
const EMITTED_TYPE_TO_OPTION: ReadonlyArray<[BlueprintEmittedTypeKey, SchemaCompareScopeCapKey]> = [
  ['tables', 'includeTables'],
  ['views', 'includeViews'],
  ['procedures', 'includeProcedures'],
  ['functions', 'includeFunctions'],
  ['sequences', 'includeSequences'],
  // S8 — capped for a SQL Server blueprint whose folder cannot hold user-type
  // files: emitted with the checkbox off, or written before 1.4.21 (see
  // `manifestRecordsUserTypeFiles`). A folder that DOES carry them compares
  // them like any other category.
  ['userTypes', 'includeUserTypes'],
];

export type FolderCompareScope = {
  /** Applied to BOTH endpoints by every caller; absent when the blueprint names no schemas. */
  schemaFilter?: SchemaFilter;
  /** Only ever contains `false` values — see `buildFolderCompareScope`. */
  typeCaps: SchemaCompareOptions;
  /** Which flags the MANIFEST capped, so they cannot be mistaken for the user's own choice. */
  capKeys: SchemaCompareScopeCapKey[];
};

/**
 * The scope of a folder whose manifest could not be read: compare everything.
 *
 * Deliberately the unscoped value rather than a refusal. A compare is read-only
 * and the user reviews any script before running it, so falling back to the
 * pre-F07 behaviour is the recoverable direction; refusing to compare at all
 * because a manifest is missing is not.
 */
export const UNSCOPED_FOLDER_COMPARE: FolderCompareScope = { typeCaps: {}, capKeys: [] };

/**
 * The manifest fields this module reads. Every one is optional even though
 * `BlueprintMetadata` requires most of them today: `readBlueprintMetadata` does
 * an unchecked `JSON.parse(raw) as BlueprintMetadata`
 * (`databaseBlueprintStore.ts`), so a manifest that predates a field or was
 * hand-edited arrives with holes the compiler never sees.
 */
export type FolderScopeMetadata = {
  includedSchemas?: string[];
  includedObjectTypes?: Partial<BlueprintObjectTypes>;
  linkedDbs?: ReadonlyArray<{ label: string; engine?: string }>;
  activeLinkLabel?: string;
  /** S8 — what the emit actually wrote. Absent on every pre-1.4.21 folder. */
  emittedTypes?: BlueprintEmittedTypeKey[];
};

/**
 * Which engine was this blueprint folder emitted for?
 *
 * The emitter gates `Sequences/*.sql` on the engine of the link it emitted from
 * (`sharedEmitter.ts`), so that is the engine the compare has to ask about — not
 * whatever the target happens to be now. Resolution mirrors
 * `utils/activeLink.resolveActiveLinkLabel`: the named link, else the manifest's
 * active link, else the first. Returns `undefined` for a manifest with no usable
 * link, which `engineHasSequences` maps to "no sequences" — the non-destructive
 * direction, documented there.
 */
export function blueprintLinkEngine(
  meta: FolderScopeMetadata,
  linkLabel: string | undefined,
): string | undefined {
  const links = meta.linkedDbs ?? [];
  const byLabel = (label: string | undefined) =>
    label ? links.find((link) => link.label === label) : undefined;
  return (byLabel(linkLabel) ?? byLabel(meta.activeLinkLabel) ?? links[0])?.engine;
}

export function buildFolderCompareScope(
  meta: FolderScopeMetadata,
  blueprintEngine: string | undefined,
): FolderCompareScope {
  const includedSchemas = meta.includedSchemas ?? [];
  const schemaFilter: SchemaFilter | undefined =
    includedSchemas.length > 0 ? { mode: 'include', schemas: [...includedSchemas] } : undefined;

  // A cap, not an assignment: an object type the blueprint does not model must
  // never be compared (its absence from the folder would read as `removed` on
  // the target), but a type the user has separately switched off in their own
  // options stays off. `false` always wins; `true` defers to the user.
  //
  // WHICH types are missing from the folder is the emitter's business, and it is
  // asked rather than modelled: `blueprintEmitsFiles` IS the predicate
  // `sharedEmitter.ts`'s write gates call. This used to be five hand-written
  // tests next to a prose citation of those gates by line number, and that
  // arrangement got four of the five backwards once already.
  //
  // `engineHasSequences` is keyed on the BLUEPRINT's own engine, not on `true`.
  // Fix round 3 hardcoded `true` here, arguing that an engine without sequences
  // has none at either endpoint. That holds only for a same-engine compare, and
  // nothing enforces one — the setup dialog's target picker lists every
  // connected connection regardless of the source's engine. A MySQL or SQLite
  // blueprint folder contains no `Sequences/*.sql` whatsoever, so reporting it
  // as emitting them left `includeSequences` uncapped; re-point the target to
  // PostgreSQL/Oracle/MSSQL through the ordinary "Change" flow and every real
  // sequence there compares against an empty folder set → `removed`: counted
  // in the verdict and selectable in the results tree, though the generator
  // emits nothing for it today — a phantom diff, not a `DROP`. See
  // `blueprintEmitsFiles`'s doc comment (merge-gate note) for what changes
  // once W1.4 lands sequence emit.
  // S8 — the third argument is this side's answer to "can the FOLDER hold
  // user-type files": a SQL Server blueprint whose manifest records an emit
  // that included them. The engine alone is not enough — the checkbox defaults
  // to on and predates the emitter's folder by three releases, so every folder
  // written before 1.4.21 would otherwise be read as holding zero types and
  // every type on the live target would read `removed` → `DROP TYPE`.
  const emits = blueprintEmitsFiles(
    meta.includedObjectTypes,
    engineHasSequences(blueprintEngine),
    engineHasUserTypes(blueprintEngine) && manifestRecordsUserTypeFiles(meta),
  );
  const typeCaps: SchemaCompareOptions = {};
  const capKeys: SchemaCompareScopeCapKey[] = [];
  for (const [type, option] of EMITTED_TYPE_TO_OPTION) {
    if (emits[type]) continue;
    // S7 — only a SQL Server blueprint has user-defined types to NOT model;
    // capping the flag for any other engine would narrow its declared scope
    // for an object kind it cannot have. See `engineHasUserTypes`. (The cap
    // itself is now conditional on the folder — see `emits` above.)
    if (type === 'userTypes' && !engineHasUserTypes(blueprintEngine)) continue;
    typeCaps[option] = false;
    capKeys.push(option);
  }
  return { schemaFilter, typeCaps, capKeys };
}

/**
 * Read a blueprint's manifest for the purpose of scoping a compare.
 *
 * Never throws and never rejects: a scope this cannot determine is
 * `undefined`, which `folderCompareScopeFor` maps to
 * `UNSCOPED_FOLDER_COMPARE`. Callers that compare several links against one
 * folder read this ONCE and pass the result to `folderCompareScopeFor` per
 * link — the manifest is per folder, only the link engine is per link.
 */
export async function readFolderScopeMetadata(
  folderPath: string,
): Promise<FolderScopeMetadata | undefined> {
  try {
    const result = await readBlueprintMetadata({ folderPath });
    return result.success ? result.metadata : undefined;
  } catch {
    return undefined;
  }
}

/**
 * `readFolderScopeMetadata`'s result → this link's scope.
 *
 * `fallbackEngine` is consulted only when the manifest has no usable link
 * (hand-edited, or written before `engine` was recorded). At every call site
 * the fallback IS the link's own database, so the two agree wherever both
 * exist.
 */
export function folderCompareScopeFor(
  meta: FolderScopeMetadata | undefined,
  linkLabel: string | undefined,
  fallbackEngine?: () => string | undefined,
): FolderCompareScope {
  if (!meta) return UNSCOPED_FOLDER_COMPARE;
  return buildFolderCompareScope(
    meta,
    blueprintLinkEngine(meta, linkLabel) ?? fallbackEngine?.(),
  );
}
